import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react'
import { useSetAtom } from 'jotai'
import { useAuth } from './AuthContext'
import {
  createWorkspace as createWorkspaceSession,
  getWorkspaceSessionSnapshot,
  initWorkspace,
  refreshMemberInfo,
  reloadWorkspaceSession,
  selectWorkspace as selectWorkspaceSession,
} from '../api/backend'
import type { CreateWorkspacePayload, MemberRole, WorkspaceSummary } from '../api/backend'
import { resetWorkspaceScopedStateAtom } from '../state/store'

// ─── Types ────────────────────────────────────────────────────────────────────

interface WorkspaceState {
  /** Whether workspace init has completed (workspaceId is available) */
  isLoading: boolean
  isResolved: boolean
  error: string | null
  workspaces: WorkspaceSummary[]
  activeWorkspace: WorkspaceSummary | null
  workspaceEntered: boolean
  workspaceId: string | null
  memberType: string | null
  memberRole: MemberRole | null
  memberName: string | null
  memberUsername: string | null
}

interface WorkspaceContextValue extends WorkspaceState {
  refreshMember: () => Promise<void>
  reloadWorkspaces: () => Promise<void>
  selectWorkspace: (workspaceId: string) => Promise<void>
  createWorkspace: (payload: CreateWorkspacePayload) => Promise<void>
}

// ─── Context ──────────────────────────────────────────────────────────────────

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null)

// ─── Provider ─────────────────────────────────────────────────────────────────

export function WorkspaceProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth()
  const resetWorkspaceScopedState = useSetAtom(resetWorkspaceScopedStateAtom)
  const [state, setState] = useState<WorkspaceState>({
    isLoading: false,
    isResolved: false,
    error: null,
    workspaces: [],
    activeWorkspace: null,
    workspaceEntered: false,
    workspaceId: null,
    memberType: null,
    memberRole: null,
    memberName: null,
    memberUsername: null,
  })

  // Prevent duplicate calls on StrictMode double-invoke
  const didInitRef = useRef(false)

  const syncStateFromCache = useCallback(() => {
    const snapshot = getWorkspaceSessionSnapshot()
    setState({
      isLoading: false,
      isResolved: Boolean(snapshot.workspaceId),
      error: null,
      workspaces: snapshot.workspaces,
      activeWorkspace: snapshot.activeWorkspace,
      workspaceEntered: snapshot.workspaceEntered,
      workspaceId: snapshot.workspaceId || null,
      memberType: snapshot.memberType,
      memberRole: snapshot.role,
      memberName: snapshot.name,
      memberUsername: snapshot.username,
    })
  }, [])

  const refreshMember = useCallback(async () => {
    await refreshMemberInfo()
    syncStateFromCache()
  }, [syncStateFromCache])

  const reloadWorkspaces = useCallback(async () => {
    setState((current) => ({ ...current, isLoading: true, error: null }))
    await reloadWorkspaceSession()
    syncStateFromCache()
  }, [syncStateFromCache])

  const selectWorkspace = useCallback(async (workspaceId: string) => {
    const previousWorkspaceId = getWorkspaceSessionSnapshot().workspaceId
    setState((current) => ({ ...current, isLoading: true, error: null }))
    try {
      await selectWorkspaceSession(workspaceId)
      if (previousWorkspaceId && previousWorkspaceId !== workspaceId) {
        resetWorkspaceScopedState()
      }
      syncStateFromCache()
    } catch (err) {
      setState((current) => ({
        ...current,
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to select workspace',
      }))
      throw err
    }
  }, [resetWorkspaceScopedState, syncStateFromCache])

  const createWorkspace = useCallback(async (payload: CreateWorkspacePayload) => {
    setState((current) => ({ ...current, isLoading: true, error: null }))
    try {
      await createWorkspaceSession(payload)
      resetWorkspaceScopedState()
      syncStateFromCache()
    } catch (err) {
      setState((current) => ({
        ...current,
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to create workspace',
      }))
      throw err
    }
  }, [resetWorkspaceScopedState, syncStateFromCache])

  useEffect(() => {
    if (!isAuthenticated) return
    if (didInitRef.current) return

    didInitRef.current = true
    setState((current) => ({ ...current, isLoading: true, error: null }))

    initWorkspace()
      .then(() => {
        syncStateFromCache()
      })
      .catch((err) => {
        console.error('WorkspaceProvider: failed to init workspace:', err)
        didInitRef.current = false
        setState({
          isLoading: false,
          isResolved: false,
          error: err instanceof Error ? err.message : 'Failed to resolve workspace',
          workspaces: [],
          activeWorkspace: null,
          workspaceEntered: false,
          workspaceId: null,
          memberType: null,
          memberRole: null,
          memberName: null,
          memberUsername: null,
        })
      })
  }, [isAuthenticated, syncStateFromCache])

  // Reset when user logs out
  useEffect(() => {
    if (!isAuthenticated) {
      didInitRef.current = false
      setState({
        isLoading: false,
        isResolved: false,
        error: null,
        workspaces: [],
        activeWorkspace: null,
        workspaceEntered: false,
        workspaceId: null,
        memberType: null,
        memberRole: null,
        memberName: null,
        memberUsername: null,
      })
    }
  }, [isAuthenticated])

  return (
    <WorkspaceContext.Provider value={{ ...state, refreshMember, reloadWorkspaces, selectWorkspace, createWorkspace }}>
      {children}
    </WorkspaceContext.Provider>
  )
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useWorkspace(): WorkspaceContextValue {
  const ctx = useContext(WorkspaceContext)
  if (!ctx) throw new Error('useWorkspace must be used within WorkspaceProvider')
  return ctx
}
